// frontend/src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CoupleProvider } from './contexts/CoupleContext';
import PrivateRoute from './components/common/PrivateRoute';
import PublicRoute from './components/common/PublicRoute';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from './pages/Header';
import PricingPage from './pages/Pricing';
import PaystackCallback from './pages/payments/PaystackCallback';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import VerificationSuccess from './pages/auth/VerificationSuccess';
import VerificationRequired from './pages/auth/VerificationRequired';
import ResendVerification from './pages/auth/ResendVerification';
import VerifyEmail from './pages/auth/VerifyEmail';
import ResetPassword from './pages/auth/ResetPassword';
import GoogleCallback from './pages/auth/GoogleCallback';
import TwitterCallback from './pages/auth/TwitterCallback';

// Admin Pages
import Dashboard from './pages/admin/Dashboard';
import CoupleSettings from './pages/admin/CoupleSettings';
import GiftRegistry from './pages/admin/GiftRegistry';
import TodoList from './pages/admin/TodoList';
import WeddingFund from './pages/admin/WeddingFund';
import UserBalance from './pages/admin/UserBalance'; // Import the new UserBalance component
import Photos from './pages/admin/Photos';
import Events from './pages/admin/Event';
import GuestList from './pages/admin/GuestList';
import CreateCouple from './pages/admin/CreateCouple';

// Visitor Pages
import WeddingHome from './pages/visitor/WeddingHome';
import VisitorRegistry from './pages/visitor/Registry';
import VisitorEvents from './pages/visitor/Event';
import VisitorPhotos from './pages/visitor/Photos';
import VisitorRSVP from './pages/visitor/RSVP';
import Contribution from './pages/visitor/Contribution';
import PaymentSuccess from './pages/visitor/PaymentSucess';
import PaymentCancel from './pages/visitor/PaymentCancel';
import ContributionVerification from './pages/visitor/ContributionVerification';

// Landing and Static Pages
import LandingPage from './pages/LangingPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';

// Error Pages
import NotFound from './pages/errors/NotFound';

// Cookie Consent
import CookieConsent from './components/visitor/CookieConsent';

// HeaderWrapper component to conditionally render the header
const HeaderWrapper = () => {
  const location = useLocation();
  
  // List of routes where the header should be shown
  const headerRoutes = ['/', '/about', '/contact', '/pricing', '/terms', '/privacy'];
  
  // Check if current path matches any of the routes that should show the header
  const shouldShowHeader = headerRoutes.includes(location.pathname);
  
  return shouldShowHeader ? <Header /> : null;
};

// Add this component before the App component
const PricingPageWrapper = () => {
  const { isAuthenticated } = useAuth();
  return <PricingPage isAuthenticated={isAuthenticated} />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <CurrencyProvider>
        <Router>
          <AuthProvider>
            <CoupleProvider>
              <HeaderWrapper />
              <Routes>
                <Route path="/" element={<LandingPage />} />
                
                {/* Auth Routes */}
                <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
               
                
                {/* Email Verification Routes - Direct access without wrappers */}
                <Route path="/verify-email" element={<VerificationSuccess />} />
                <Route path="/verification-required" element={<VerificationRequired />} />
                <Route path="/resend-verification" element={<ResendVerification />} />
                <Route path="/auth/verify" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/auth/google/callback" element={<GoogleCallback />} />
                <Route path="/auth/twitter/callback" element={<TwitterCallback />} />
                <Route path="/payments/paystack/callback" element={<PaystackCallback />} />
                <Route path="/contribution-verify" element={<ContributionVerification />} />
    
                
                {/* Static Pages */}
                <Route path="/about" element={<AboutPage />} />
                <Route path="/pricing" element={<PricingPageWrapper />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                
                {/* Admin Routes */}
                <Route path="/admin" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                <Route path="/admin/create-couple" element={<PrivateRoute><CreateCouple /></PrivateRoute>} />
                <Route path="/admin/couple-settings" element={<PrivateRoute><CoupleSettings /></PrivateRoute>} />
                <Route path="/admin/registry" element={<PrivateRoute><GiftRegistry /></PrivateRoute>} />
                <Route path="/admin/todos" element={<PrivateRoute><TodoList /></PrivateRoute>} />
                <Route path="/admin/wedding-fund" element={<PrivateRoute><WeddingFund /></PrivateRoute>} />
                <Route path="/admin/balance" element={<PrivateRoute><UserBalance /></PrivateRoute>} /> {/* New Balance route */}
                <Route path="/admin/photos" element={<PrivateRoute><Photos /></PrivateRoute>} />
                <Route path="/admin/events" element={<PrivateRoute><Events /></PrivateRoute>} />
                <Route path="/admin/guests" element={<PrivateRoute><GuestList /></PrivateRoute>} />
                
                {/* Wedding visitor routes */}
                <Route path="/wedding/:customUrl" element={<WeddingHome />} />
                <Route path="/wedding/:customUrl/registry" element={<VisitorRegistry />} />
                <Route path="/wedding/:customUrl/events" element={<VisitorEvents />} />
                <Route path="/wedding/:customUrl/photos" element={<VisitorPhotos />} />
                <Route path="/wedding/:customUrl/rsvp" element={<VisitorRSVP />} />
                <Route path="/wedding/:customUrl/contribute" element={<Contribution />} />
                <Route path="/wedding/:customUrl/payment/success" element={<PaymentSuccess />} />
                <Route path="/wedding/:customUrl/payment/cancel" element={<PaymentCancel />} />
                
                {/* 404 Route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              
              <ToastContainer 
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
              />
              <CookieConsent />
            </CoupleProvider>
          </AuthProvider>
        </Router>
      </CurrencyProvider>
    </ThemeProvider>
  );
};

export default App;